import { useState } from 'react';

import type { UpdateWages } from '@web/onboarding/Checklist/onboardingChecklist.types';
import {
  RestangularPosition,
  RestangularEmployee,
  Tab,
} from '@web/onboarding/Checklist/DefineWagesModal/defineWagesModal.types';
import useEmployeesWages from '@web/onboarding/Checklist/DefineWagesModal/hooks/useEmployeesWages';
import usePositionWages from '@web/onboarding/Checklist/DefineWagesModal/hooks/usePositionWages';
import { isFieldDirty } from '@web/onboarding/Checklist/DefineWagesModal/defineWagesModal.utils';

type Props = {
  afterSubmitCallback?: () => void;
  positions: RestangularPosition[];
  employees: RestangularEmployee[];
  updateWages: UpdateWages;
};
const useEmployeesAndPositionsTabs = ({
  afterSubmitCallback,
  positions,
  employees,
  updateWages,
}: Props) => {
  const [hasLeftEmployeesTab, setHasLeftEmployeesTab] = useState(false);
  const [hasLeftPositionsTab, setHasLeftPositionsTab] = useState(false);
  const [currentTab, setCurrentTab] = useState<Tab>(Tab.employees);
  const {
    fields: employeesFields,
    submitWages: employeesSubmit,
    errors: employeesErrors,
  } = useEmployeesWages({
    employees,
    updateWages,
    afterSubmitCallback,
  });

  const {
    fields: positionsFields,
    submitWages: positionsSubmit,
    errors: positionsErrors,
  } = usePositionWages({
    positions,
    updateWages,
    afterSubmitCallback,
  });

  const isAnyEmployeeWageModified = employeesFields.some(({ wage }) => isFieldDirty(wage));
  const isAnyPositionWageModified = positionsFields.some(({ wage }) => isFieldDirty(wage));

  const isEmployeesTabDirty = hasLeftEmployeesTab && isAnyEmployeeWageModified;
  const isPositionsTabDirty = hasLeftPositionsTab && isAnyPositionWageModified;
  const shouldDisplayWarning =
    (currentTab === Tab.employees && isEmployeesTabDirty) ||
    (currentTab === Tab.positions && isPositionsTabDirty);

  const onTabChange = (tabIndex) => {
    if (tabIndex === 1) {
      // changed from employees to positions
      setHasLeftEmployeesTab(true);
      setCurrentTab(Tab.positions);
    } else {
      setHasLeftPositionsTab(true);
      setCurrentTab(Tab.employees);
    }
  };

  return {
    employeesFields,
    employeesSubmit,
    employeesErrors,
    positionsFields,
    positionsSubmit,
    positionsErrors,
    isEmployeesTabDirty,
    isPositionsTabDirty,
    onTabChange,
    shouldDisplayWarning,
  };
};

export default useEmployeesAndPositionsTabs;
